import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
// import "pdfjs-dist/web/pdf_viewer.css";
import "./PdfViewer.css";
import { GetFileNameFromUrl } from "../../utils/utils";
import Selector from "./Selector";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
const samplePdfUrl = `${process.env.PUBLIC_URL}/sample.pdf`; // Path to the sample PDF in public folder

const PdfViewer = ({ pdfResponse = samplePdfUrl, onLoadComplete }) => {
  //#region VARS --------------------------------------------------------
  const [loadingDoc, setLoadingDoc] = useState(true);
  const canvasRef = useRef(null);
  const pageRefs = useRef([]);
  const docTitle = useRef("");

  const [thumbsnailsCollapsed, setThumbsnailsCollapsed] = useState(true);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.65);

  const inputPage = useRef();
  const [inputPageValue, setInputPageValue] = useState("1");

  const inputZoom = useRef();
  const [inputZoomValue, setInputZoomValue] = useState(
    `${(scale * 100).toLocaleString("es")}`
  );
  const zoomRange = [0.2, 3.0]; //20%-300%
  const zoomStep = 0.05;

  const pdfsRef = useRef();
  const pdfSelectorOptions = useRef();
  const [selectedPdf, setSelectedPdf] = useState({ value: 0 });
  //#endregion VARS -----------------------------------------------------

  useEffect(() => {
    const fetchAndProcessPdfs = async () => {
      // Ensure pdfDocs.current is an array
      const docsToFetch = pdfResponse.docs ?? [
        { url: pdfResponse.url, version: pdfResponse.version },
      ];

      // console.log("docsToFetch:", docsToFetch);

      // Create an array of promises for fetching and processing each PDF
      const pdfPromises = docsToFetch.map(async (doc) => {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

        // Return both blob and pdf document for further processing
        return { blob, pdf, doc };
      });

      try {
        // Wait for all promises to resolve
        const processedPdfs = await Promise.all(pdfPromises);
        // console.log("processedPdfs:", processedPdfs);
        pdfsRef.current = processedPdfs;

        pdfSelectorOptions.current = processedPdfs.map((doc, index) => ({
          value: index,
          label: `${doc.doc.name} (v${doc.doc.version})`,
        }));

        // Handle results in order. Update refs or state with all blobs and generate all thumbnails
        // processedPdfs.forEach((doc) => {
        //   generateThumbnails(doc);
        // });
        await Promise.all(processedPdfs.map((doc) => generateThumbnails(doc)));
        // console.log("pdfsRef:", pdfsRef.current);

        if (onLoadComplete) {
          onLoadComplete();
        }
        setLoadingDoc(false);
      } catch (error) {
        console.error("Failed to fetch or process PDFs:", error);
        if (onLoadComplete) onLoadComplete();
        setLoadingDoc(false);
      }
    };

    if (pdfResponse) {
      fetchAndProcessPdfs();
    }
  }, [pdfResponse, onLoadComplete]);

  async function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf.numPages);
    const pdfMetadata = await pdf.getMetadata();
    docTitle.current =
      pdfsRef.current[selectedPdf?.value]?.doc?.name ||
      pdfMetadata?.info["Title"] ||
      GetFileNameFromUrl(pdfsRef.current[selectedPdf?.value]?.doc?.url);
  }

  const generateThumbnails = async (doc) => {
    const thumbs = [];
    const pdf = doc.pdf;
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 0.3 });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;
      thumbs.push(canvas.toDataURL());
    }

    doc.thumbs = thumbs;
    // setThumbnails([...thumbs, thumbs]);
    setThumbsnailsCollapsed(false);
  };

  const downloadPdf = async () => {
    const blob = pdfsRef.current[selectedPdf?.value].blob;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = docTitle.current;
    link.click();
  };

  //#region HANDLE PAGES --------------------------------------------------------
  function changePage(offset) {
    setPageNumber((prevPageNumber) => {
      const newPage = Math.max(1, Math.min(numPages, prevPageNumber + offset));
      setInputPageValue(newPage.toString());
      inputPage.current.value = newPage;
      return newPage;
    });
  }

  function goToPage(pageNum) {
    // setPageNum(pageNum);
    setPageNumber(pageNum);
    setInputPageValue(pageNum.toString());
  }

  const confirmInputPageChange = () => {
    let parsed = parseInt(inputPageValue);
    parsed = Math.max(1, Math.min(numPages, parsed));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= numPages) {
      setInputPageValue(parsed.toString());
      setPageNumber(parsed);
    } else {
      setInputPageValue(parsed.toString());
    }
  };

  const handleInputPageChange = (e) => {
    // Let the user type freely, even incomplete values
    setInputPageValue(e.target.value);
  };

  const handleInputPageEnter = (e) => {
    if (e.key === "Enter") {
      confirmInputPageChange();
    }
  };

  useEffect(() => {
    if (pageRefs.current[pageNumber - 1]) {
      pageRefs.current[pageNumber - 1].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [pageNumber]);
  //#endregion HANDLE PAGES -----------------------------------------------------

  //#region HANDLE ZOOM --------------------------------------------------------
  function zoomIn() {
    // setScale((prevScale) => Math.min(prevScale + zoomStep, zoomRange[1]));
    setScale((prevScale) => {
      const newZoom = Math.min(prevScale + zoomStep, zoomRange[1]);
      setInputZoomValue((newZoom * 100).toLocaleString("es"));
      return newZoom;
    });
  }

  function zoomOut() {
    setScale((prevScale) => {
      const newZoom = Math.max(prevScale - zoomStep, zoomRange[0]);
      setInputZoomValue((newZoom * 100).toLocaleString("es"));
      return newZoom;
    });
  }

  const confirmInputZoomChange = () => {
    let parsed = parseInt(inputZoomValue) / 100;
    parsed = Math.max(zoomRange[0], Math.min(zoomRange[1], parsed));
    if (!isNaN(parsed) && parsed >= zoomRange[0] && parsed <= zoomRange[1]) {
      setInputZoomValue((parsed * 100).toLocaleString("es"));
      setScale(parsed);
    } else {
      setInputZoomValue((parsed * 100).toLocaleString("es"));
    }
  };

  const handleInputZoomChange = (e) => {
    // Let the user type freely, even incomplete values
    setInputZoomValue(e.target.value);
  };

  const handleInputZoomEnter = (e) => {
    if (e.key === "Enter") {
      confirmInputZoomChange();
    }
  };
  //#endregion HANDLE ZOOM -----------------------------------------------------

  if (!pdfsRef.current || loadingDoc) return null;

  // console.log("selectedPdf", selectedPdf);
  // console.log("selectedPdf", pdfsRef.current[selectedPdf?.value]);

  return (
    <div className="pdf-container">
      {/* PDF Title */}
      <div className="pdf-titles-container">
        {pdfsRef.current.length > 1 ? (
          <Selector
            callback={setSelectedPdf}
            defaultValue={pdfSelectorOptions.current[0] ?? null}
            isClearable={false}
            options={pdfsRef.current.map((doc, index) => ({
              value: index,
              label: `${doc.doc.name} (v${doc.doc.version})`,
            }))}
          />
        ) : (
          <span className="pdf-title">
            {pdfsRef.current[selectedPdf?.value]?.doc?.name ??
              GetFileNameFromUrl(pdfsRef.current[selectedPdf?.value]?.doc?.url)}
            {pdfsRef.current[selectedPdf?.value]?.doc?.version
              ? ` (v${pdfsRef.current[selectedPdf?.value]?.doc?.version})`
              : null}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="pdf-controls">
        <div className="controls left">
          {/* Collapse Thumbs */}
          <button
            onClick={() =>
              setThumbsnailsCollapsed(
                (thumbsnailsCollapsed) => !thumbsnailsCollapsed
              )
            }
            title={`${thumbsnailsCollapsed ? "Abrir" : "Cerrar"}`}
          >
            <img
              style={{ width: "20px" }}
              src="imgs/thumbnails-svgrepo-com.svg"
              // src="https://archibot-hub.s3.amazonaws.com/_ui-Assets/View_01_Grid.png"
              alt=""
            />
          </button>
        </div>

        <div className="controls mid">
          {/* Next/Prev Page */}
          <input
            ref={inputPage}
            className="input-page"
            type="number"
            id="pageNumber"
            value={inputPageValue}
            onChange={handleInputPageChange}
            onKeyDown={handleInputPageEnter}
            onBlur={confirmInputPageChange}
            min="1"
            max={numPages}
          />
          <span>/ {numPages || "--"}</span>
          <button onClick={() => changePage(-1)} disabled={pageNumber <= 1}>
            <img
              src="https://mozilla.github.io/pdf.js/legacy/web/images/toolbarButton-pageUp.svg"
              alt=""
            />
            <span>Page Up</span>
          </button>
          <button
            onClick={() => changePage(1)}
            disabled={pageNumber >= (numPages || 1)}
          >
            <img
              src="https://mozilla.github.io/pdf.js/legacy/web/images/toolbarButton-pageDown.svg"
              alt=""
            />
          </button>

          {/* Separator Line */}
          <div className="vl"></div>

          {/* Zoom */}
          <button
            onClick={zoomOut}
            disabled={scale <= zoomRange[0]}
            title="Zoom Out"
          >
            <img
              src="https://mozilla.github.io/pdf.js/legacy/web/images/toolbarButton-zoomOut.svg"
              alt=""
            />
          </button>
          <button
            onClick={zoomIn}
            disabled={scale >= zoomRange[1]}
            title="Zoom In"
          >
            <img
              src="https://mozilla.github.io/pdf.js/legacy/web/images/toolbarButton-zoomIn.svg"
              alt=""
            />
          </button>
          <input
            ref={inputZoom}
            className="input-page"
            type="number"
            id="zoomNumber"
            value={inputZoomValue}
            onChange={handleInputZoomChange}
            onKeyDown={handleInputZoomEnter}
            onBlur={confirmInputZoomChange}
            min="1"
            max={numPages}
          />
          <span>{/* (scale * 100).toFixed(0) */}%</span>
        </div>

        <div className="controls right">
          {/* Download */}
          <button onClick={downloadPdf} title="Descargar">
            <img
              className="controls-download"
              src="https://archibot-hub.s3.amazonaws.com/_ui-Assets/Descargar.svg"
              alt=""
            />
          </button>
        </div>
      </div>

      {/*TODO: Revisar si agregar un iFrame para el pdf es mejor y utilizar el pdfViewer del navegador.*/}
      {/* <iframe
          src={transformAssetsPath(url)}
          className="pdf-viewer-iframe"
          title="PDF Viewer"
      /> */}

      {/* Thumbnails */}
      <div
        className={`pdf-thumbnails-panel ${
          thumbsnailsCollapsed ? "collapsed" : ""
        }`}
      >
        {pdfsRef.current[selectedPdf?.value]?.thumbs?.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`Thumbnail ${i + 1}`}
            className={`pdf-thumbnail ${
              pageNumber === i + 1 ? "selected" : ""
            }`}
            onClick={() => goToPage(i + 1)}
          />
        ))}
      </div>

      {/* PDF Viewer */}
      <div
        className={`pdf-main ${thumbsnailsCollapsed ? "collapsed" : ""}`}
        ref={canvasRef}
      >
        <Document
          className={"pdf-document"}
          file={pdfsRef.current[selectedPdf?.value]?.blob}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div>Loading PDF...</div>}
          error={<div>Error loading PDF. Please ensure pdf file is valid.</div>}
        >
          {Array.from(new Array(numPages), (_, index) => (
            <div
              key={`page_${index + 1}`}
              ref={(el) => (pageRefs.current[index] = el)}
              className={`pdf-page-wrapper ${
                pageNumber === index + 1 ? "active" : ""
              }`}
            >
              <Page
                pageNumber={index + 1}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;
